import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Server, Activity, Cpu, HardDrive, Wifi, WifiOff, AlertTriangle, CheckCircle, Clock, Users } from 'lucide-react';
import api from '../services/api';
import { format } from 'date-fns';

const Monitoring: React.FC = () => {
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  
  // selectedServer can be used for showing server details in the future
  console.log(selectedServer);
  
  const { data: serversStatus } = useQuery({
    queryKey: ['servers-status'],
    queryFn: api.getServersStatus,
    refetchInterval: 10000,
  });

  const { data: agentStatus } = useQuery({
    queryKey: ['agent-status'],
    queryFn: api.getAgentStatus,
    refetchInterval: 10000,
  });

  const servers = [
    {
      id: 'server1',
      name: 'IMAP Agents',
      location: 'Germany',
      specs: `${agentStatus?.imap?.total || 0} Agents`,
      type: 'imap',
      agents: { busy: agentStatus?.imap?.busy || 0, online: agentStatus?.imap?.online || 0, total: agentStatus?.imap?.total || 0 }
    },
    {
      id: 'server2',
      name: 'Domain Agents',
      location: 'Germany',
      specs: `${agentStatus?.domain?.total || 0} Agents`,
      type: 'domain',
      agents: { busy: agentStatus?.domain?.busy || 0, online: agentStatus?.domain?.online || 0, total: agentStatus?.domain?.total || 0 }
    },
    {
      id: 'server3',
      name: 'Warmup Agents',
      location: 'Germany',
      specs: `${agentStatus?.warmup?.total || 0} Agents`,
      type: 'warmup',
      agents: { busy: agentStatus?.warmup?.busy || 0, online: agentStatus?.warmup?.online || 0, total: agentStatus?.warmup?.total || 0 }
    },
    {
      id: 'server4',
      name: 'Domain Coordinator',
      location: 'Germany',
      specs: 'FastAPI Service',
      type: 'coordinator'
    },
    {
      id: 'server5',
      name: 'IMAP Coordinator + SMS',
      location: 'Germany',
      specs: 'Django + Node.js',
      type: 'coordinator'
    },
  ];

  const coordinators = [
    {
      name: 'Domain Coordinator 1',
      url: 'http://46.4.35.162:8000',
      service: 'FastAPI',
      endpoints: ['Domain Processing', 'DKIM Setup', 'Cloudflare'],
      status: serversStatus?.['Domain Coordinator 1']
    },
    {
      name: 'IMAP Coordinator 1',
      url: 'http://23.88.70.182:5000',
      service: 'Django',
      endpoints: ['IMAP Configuration', 'Account Setup'],
      status: serversStatus?.['IMAP Coordinator 1']
    },
    {
      name: 'Warmup Coordinator 1',
      url: 'http://94.130.130.221:8001',
      service: 'FastAPI',
      endpoints: ['Instantly Reconnect', 'Smartlead Reconnect', 'Bison'],
      status: serversStatus?.['Warmup Coordinator 1']
    },
    {
      name: 'SMS Backend',
      url: 'http://23.88.70.182:5001',
      service: 'Node.js',
      endpoints: ['SMS Verification', 'Phone Numbers'],
      status: serversStatus?.['SMS Backend']
    },
    {
      name: 'Bulk User Service',
      url: 'http://178.63.40.107:6123',
      service: 'Go Service',
      endpoints: ['User Creation', 'Batch Processing'],
      status: serversStatus?.['Bulk User Service']
    },
  ];

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'reachable':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'unreachable':
        return <WifiOff className="w-5 h-5 text-red-400" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'reachable':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'unreachable':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'degraded':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold premium-gradient">System Monitoring</h1>
        <p className="text-gray-400 mt-1">Real-time infrastructure and agent monitoring</p>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">System Health</div>
            <Activity className="w-5 h-5 text-green-400" />
          </div>
          <div className="text-2xl font-bold text-white">Operational</div>
          <div className="text-xs text-gray-400 mt-1">All systems running</div>
        </div>
        <div className="stat-card stat-card-orange">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-orange-500">IMAP Agents</div>
            <Users className="w-5 h-5 text-orange-400" />
          </div>
          <div className="text-2xl font-bold text-white">
            {agentStatus?.imap?.online || 0}
          </div>
          <div className="text-xs text-gray-400 mt-1">Online</div>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Domain Agents</div>
            <Server className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-white">
            {agentStatus?.domain?.online || 0}
          </div>
          <div className="text-xs text-gray-400 mt-1">Online</div>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Warmup Agents</div>
            <Activity className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-white">
            {agentStatus?.warmup?.online || 0}
          </div>
          <div className="text-xs text-gray-400 mt-1">Online</div>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Avg Response</div>
            <Clock className="w-5 h-5 text-yellow-400" />
          </div>
          <div className="text-2xl font-bold text-white">
            {serversStatus ?
              Math.round(Object.values(serversStatus).reduce((acc: number, s: any) => acc + (s.response_time_ms || 0), 0) /
              Object.keys(serversStatus).length) : 0}ms
          </div>
          <div className="text-xs text-gray-400 mt-1">Across all services</div>
        </div>
      </div>

      {/* Server Infrastructure */}
      <div className="dashboard-card">
        <h2 className="text-xl font-semibold text-white mb-4">Server Infrastructure</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {servers.map((server) => (
            <div
              key={server.id}
              className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50 hover:border-orange-500/30 transition-all cursor-pointer"
              onClick={() => setSelectedServer(server.id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-white">{server.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{server.location}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Wifi className="w-4 h-4 text-green-400" />
                  <span className="text-xs text-green-400">Online</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="text-xs text-gray-400">{server.specs}</div>
                
                {server.agents && (
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">Agent Utilization</span>
                      <span className="text-white font-medium">
                        {server.agents.busy}/{server.agents.online} busy
                      </span>
                    </div>
                    <div className="bg-gray-900 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 transition-all duration-300"
                        style={{ width: `${server.agents.online ? (server.agents.busy / server.agents.online) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {server.type === 'imap' && (
                  <div className="flex items-center gap-2 text-xs">
                    <Cpu className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-400">64GB RAM</span>
                    <HardDrive className="w-3 h-3 text-gray-500 ml-2" />
                    <span className="text-gray-400">500GB SSD</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coordinator Services */}
      <div className="dashboard-card">
        <h2 className="text-xl font-semibold text-white mb-4">Coordinator Services</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="px-6 py-3 text-left">Service</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">URL</th>
                <th className="px-6 py-3 text-left">Response Time</th>
                <th className="px-6 py-3 text-left">Endpoints</th>
                <th className="px-6 py-3 text-left">Last Check</th>
              </tr>
            </thead>
            <tbody>
              {coordinators.map((coord) => (
                <tr key={coord.name} className="table-row">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-white">{coord.name}</div>
                      <div className="text-xs text-gray-500">{coord.service}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(coord.status?.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                        getStatusColor(coord.status?.status)
                      }`}>
                        {coord.status?.status || 'Unknown'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-xs text-gray-400">{coord.status?.service || coord.url}</span>
                  </td>
                  <td className="px-6 py-4">
                    {coord.status?.response_time_ms ? (
                      <span className={`text-sm font-medium ${
                        coord.status.response_time_ms < 100 ? 'text-green-400' :
                        coord.status.response_time_ms < 500 ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {coord.status.response_time_ms}ms
                      </span>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {coord.endpoints.map((endpoint) => (
                        <span key={endpoint} className="px-2 py-0.5 bg-gray-800 rounded text-xs text-gray-400">
                          {endpoint}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {coord.status?.last_checked ? (
                      <span className="text-xs text-gray-400">
                        {format(new Date(coord.status.last_checked), 'HH:mm:ss')}
                      </span>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Agent Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-white mb-4">IMAP Agents</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-400">Utilization</span>
                <span className="text-sm font-medium text-white">
                  {agentStatus?.imap?.busy || 0}/{agentStatus?.imap?.online || 0} busy · {agentStatus?.imap?.online || 0}/{agentStatus?.imap?.total || 0} online
                </span>
              </div>
              <div className="bg-gray-800 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 transition-all duration-300"
                  style={{
                    width: `${agentStatus?.imap?.online ? (agentStatus.imap.busy / agentStatus.imap.online) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-white mb-4">Domain Agents</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-400">Utilization</span>
                <span className="text-sm font-medium text-white">
                  {agentStatus?.domain?.busy || 0}/{agentStatus?.domain?.online || 0} busy · {agentStatus?.domain?.online || 0}/{agentStatus?.domain?.total || 0} online
                </span>
              </div>
              <div className="bg-gray-800 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
                  style={{
                    width: `${agentStatus?.domain?.online ? (agentStatus.domain.busy / agentStatus.domain.online) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-white mb-4">Warmup Agents</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-400">Utilization</span>
                <span className="text-sm font-medium text-white">
                  {agentStatus?.warmup?.busy || 0}/{agentStatus?.warmup?.online || 0} busy · {agentStatus?.warmup?.online || 0}/{agentStatus?.warmup?.total || 0} online
                </span>
              </div>
              <div className="bg-gray-800 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                  style={{
                    width: `${agentStatus?.warmup?.online ? (agentStatus.warmup.busy / agentStatus.warmup.online) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-white mb-4">System Metrics</h3>
          {(() => {
            const totalAgents = (agentStatus?.imap?.total || 0) + (agentStatus?.domain?.total || 0) + (agentStatus?.warmup?.total || 0);
            const onlineAgents = (agentStatus?.imap?.online || 0) + (agentStatus?.domain?.online || 0) + (agentStatus?.warmup?.online || 0);
            const busyAgents = (agentStatus?.imap?.busy || 0) + (agentStatus?.domain?.busy || 0) + (agentStatus?.warmup?.busy || 0);
            return (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Total Agents</div>
                  <div className="text-2xl font-bold text-white">{totalAgents}</div>
                  <div className="text-xs text-gray-400">Across all types</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Online Agents</div>
                  <div className="text-2xl font-bold text-orange-400">{onlineAgents}</div>
                  <div className="text-xs text-gray-400">Currently online</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Services Online</div>
                  <div className="text-2xl font-bold text-green-400">
                    {serversStatus ?
                      Object.values(serversStatus).filter((s: any) => s.status === 'reachable').length : 0}
                  </div>
                  <div className="text-xs text-gray-400">Out of {serversStatus ? Object.keys(serversStatus).length : 0}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Utilization Rate</div>
                  <div className="text-2xl font-bold text-white">
                    {onlineAgents ? Math.round((busyAgents / onlineAgents) * 100) : 0}%
                  </div>
                  <div className="text-xs text-gray-400">{busyAgents} busy / {onlineAgents} online</div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default Monitoring;